import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';

export const CookiePolicy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-black text-white">
      <div className="container mx-auto px-6 py-12 max-w-4xl">
        <Button
          onClick={() => navigate('/')}
          variant="ghost"
          className="mb-8 text-gray-400 hover:text-white"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Torna alla Home
        </Button>

        <h1 className="text-4xl font-bold mb-8">Cookie Policy</h1>
        <p className="text-gray-400 mb-8">Ultimo aggiornamento: {new Date().toLocaleDateString('it-IT')}</p>

        <div className="space-y-8 text-gray-300 leading-relaxed">
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">1. Cosa sono i Cookie</h2>
            <p>
              I cookie sono piccoli file di testo che vengono memorizzati sul tuo dispositivo (computer, tablet,
              smartphone) quando visiti un sito web. I cookie consentono al sito di riconoscerti durante visite
              successive e di ricordare le tue preferenze.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">2. Tipologie di Cookie Utilizzati</h2>
            
            <h3 className="text-xl font-semibold text-white mb-3 mt-6">2.1 Cookie Tecnici (Necessari)</h3>
            <p className="mb-4">
              Questi cookie sono essenziali per il funzionamento del sito e non possono essere disabilitati nei nostri
              sistemi. Sono generalmente impostati solo in risposta ad azioni da te effettuate che costituiscono una
              richiesta di servizi.
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Cookie di sessione:</strong> Permettono la navigazione e l'utilizzo del sito</li>
              <li><strong>Cookie di preferenze lingua:</strong> Memorizzano la lingua selezionata</li>
              <li><strong>Cookie di sicurezza:</strong> Proteggono da accessi non autorizzati</li>
            </ul>
            <p className="mt-4 bg-zinc-900 p-4 rounded-none border-l-4 border-white">
              <strong>Questi cookie non richiedono il consenso dell'utente</strong> ai sensi del Provvedimento del
              Garante Privacy dell'8 maggio 2014.
            </p>

            <h3 className="text-xl font-semibold text-white mb-3 mt-6">2.2 Cookie Analitici</h3>
            <p className="mb-4">
              Questi cookie ci aiutano a capire come i visitatori interagiscono con il sito, fornendoci informazioni
              sulle aree visitate, il tempo trascorso sul sito e eventuali problemi riscontrati.
            </p>
            
            <div className="bg-zinc-900 p-6 rounded-none mb-4">
              <h4 className="text-lg font-semibold text-white mb-2">Google Analytics</h4>
              <p className="mb-2"><strong>Finalità:</strong> Analisi del traffico e comportamento degli utenti</p>
              <p className="mb-2"><strong>Tipologia:</strong> Cookie analitici</p>
              <p className="mb-2"><strong>Cookie utilizzati:</strong> _ga, _gid, _gat</p>
              <p className="mb-2"><strong>Durata:</strong></p>
              <ul className="list-disc pl-6 mb-2">
                <li>_ga: 2 anni</li>
                <li>_gid: 24 ore</li>
                <li>_gat: 1 minuto</li>
              </ul>
              <p className="mb-2"><strong>Dati raccolti:</strong> Indirizzo IP (anonimizzato), pagine visitate, durata della visita, tipo di dispositivo, browser utilizzato</p>
              <p className="mb-2"><strong>Base giuridica:</strong> Consenso dell'utente (Art. 6.1.a GDPR)</p>
              <p className="mb-2">
                <strong>Privacy Policy:</strong>{' '}
                <a
                  href="https://policies.google.com/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white underline"
                >
                  https://policies.google.com/privacy
                </a>
              </p>
              <p className="mb-2">
                <strong>Opt-out:</strong>{' '}
                <a
                  href="https://tools.google.com/dlpage/gaoptout"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white underline"
                >
                  Disattiva Google Analytics
                </a>
              </p>
            </div>

            <h3 className="text-xl font-semibold text-white mb-3 mt-6">2.3 Cookie di Terze Parti</h3>
            <p className="mb-4">Il nostro sito utilizza servizi di terze parti che possono installare cookie:</p>
            
            <div className="bg-zinc-900 p-6 rounded-none mb-4">
              <h4 className="text-lg font-semibold text-white mb-2">Voiceflow (Chatbot)</h4>
              <p className="mb-2"><strong>Finalità:</strong> Fornire assistenza tramite chatbot</p>
              <p className="mb-2"><strong>Tipologia:</strong> Cookie funzionali</p>
              <p className="mb-2"><strong>Durata:</strong> Sessione</p>
              <p className="mb-2">
                <strong>Privacy Policy:</strong>{' '}
                <a
                  href="https://www.voiceflow.com/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white underline"
                >
                  https://www.voiceflow.com/privacy
                </a>
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">3. Durata dei Cookie</h2>
            <p className="mb-4">I cookie possono essere:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <strong>Cookie di sessione:</strong> Vengono eliminati automaticamente alla chiusura del browser
              </li>
              <li>
                <strong>Cookie persistenti:</strong> Rimangono memorizzati sul dispositivo fino alla loro scadenza o
                fino a quando vengono eliminati manualmente
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">4. Come Gestire i Cookie</h2>
            <p className="mb-4">
              Puoi gestire o eliminare i cookie secondo le tue preferenze. Puoi eliminare tutti i cookie già presenti
              sul tuo dispositivo e impostare la maggior parte dei browser per impedirne l'installazione.
            </p>

            <h3 className="text-xl font-semibold text-white mb-3 mt-6">4.1 Gestione tramite Browser</h3>
            <p className="mb-4">Puoi modificare le impostazioni dei cookie tramite il tuo browser:</p>
            
            <div className="space-y-4">
              <div className="bg-zinc-900 p-4 rounded-none">
                <p className="font-semibold text-white mb-2">Google Chrome</p>
                <p>Impostazioni → Privacy e sicurezza → Cookie e altri dati dei siti</p>
                <a
                  href="https://support.google.com/chrome/answer/95647"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white underline text-sm"
                >
                  Guida ufficiale
                </a>
              </div>

              <div className="bg-zinc-900 p-4 rounded-none">
                <p className="font-semibold text-white mb-2">Mozilla Firefox</p>
                <p>Opzioni → Privacy e sicurezza → Cookie e dati dei siti web</p>
                <a
                  href="https://support.mozilla.org/it/kb/Gestione%20dei%20cookie"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white underline text-sm"
                >
                  Guida ufficiale
                </a>
              </div>

              <div className="bg-zinc-900 p-4 rounded-none">
                <p className="font-semibold text-white mb-2">Safari</p>
                <p>Preferenze → Privacy → Cookie e dati di siti web</p>
                <a
                  href="https://support.apple.com/it-it/guide/safari/sfri11471/mac"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white underline text-sm"
                >
                  Guida ufficiale
                </a>
              </div>

              <div className="bg-zinc-900 p-4 rounded-none">
                <p className="font-semibold text-white mb-2">Microsoft Edge</p>
                <p>Impostazioni → Cookie e autorizzazioni sito → Cookie e dati del sito</p>
                <a
                  href="https://support.microsoft.com/it-it/microsoft-edge/eliminare-i-cookie-in-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white underline text-sm"
                >
                  Guida ufficiale
                </a>
              </div>
            </div>

            <div className="mt-6 bg-yellow-900/20 border border-yellow-600/50 p-6 rounded-none">
              <p className="text-yellow-200">
                <strong>⚠️ Attenzione:</strong> La disabilitazione dei cookie tecnici potrebbe compromettere alcune
                funzionalità del sito, come la memorizzazione della lingua selezionata o il funzionamento del chatbot.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">5. Cookie di Profilazione e Marketing</h2>
            <p>
              <strong>Attualmente non utilizziamo cookie di profilazione o marketing.</strong> Non tracciamo i tuoi
              comportamenti di navigazione per finalità pubblicitarie.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">6. Modifiche alla Cookie Policy</h2>
            <p>
              Questa Cookie Policy può essere aggiornata periodicamente. Ti invitiamo a consultare regolarmente questa
              pagina per essere informato sulle modalità con cui utilizziamo i cookie.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">7. Ulteriori Informazioni</h2>
            <p className="mb-4">
              Per maggiori informazioni sul trattamento dei dati personali, consulta la nostra{' '}
              <a href="/privacy-policy" className="text-white underline">Privacy Policy</a>.
            </p>
            <p>
              Per domande o richieste relative ai cookie, contattaci a:{' '}
              <a href="mailto:info@digitalperformances.it" className="text-white underline">
                info@digitalperformances.it
              </a> o via WhatsApp al{' '}
              <a href="https://wa.me/393793460226" target="_blank" rel="noopener noreferrer" className="text-white underline">
                +39 379 346 0226
              </a>
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">8. Risorse Utili</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <a
                  href="https://www.garanteprivacy.it/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white underline"
                >
                  Garante per la Protezione dei Dati Personali
                </a>
              </li>
              <li>
                <a
                  href="https://www.youronlinechoices.com/it/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white underline"
                >
                  Your Online Choices - Gestione cookie pubblicitari
                </a>
              </li>
              <li>
                <a
                  href="https://www.allaboutcookies.org/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white underline"
                >
                  All About Cookies - Informazioni sui cookie
                </a>
              </li>
            </ul>
          </section>
        </div>
      </div>
    </div>
  );
};